import { itemKey, type CraftingResolverService, type ItemDef } from "../../services/craftingResolver";
import type { StorageSnapshot, ResolveResult, ItemStackDto, CustomItemDefinition } from "../../services/inventoryApi";
import type { ItemStack } from "../../models/itemStack";
import { fromItemStackDto } from "../../services/itemRefAdapters";
import { resolveItemDisplayName } from "../../services/itemDisplayName";

export function snapQty(snap: StorageSnapshot, key: string): number {
  if (key.includes(":")) return snap[key] ?? 0;
  const prefix = `${key}:`;
  let total = 0;
  for (const k in snap) {
    if (k.startsWith(prefix)) total += snap[k];
  }
  return total;
}


export const VIEW_KEY = "vektor.inventory.view";

(() => {
  const old = localStorage.getItem("remotex.inventory.view");
  if (old !== null) {
    localStorage.setItem("vektor.inventory.view", old);
    localStorage.removeItem("remotex.inventory.view");
  }
})();

export type SortMode = "name" | "qty-desc" | "qty-asc" | "id";

export interface ViewState {
  dualView: boolean;
  leftStorageId: string | null;
  rightStorageId: string | null;
  leftSort: SortMode;
  rightSort: SortMode;
  leftSearch: string;
  rightSearch: string;
}

export function loadViewState(): ViewState {
  try {
    const raw = localStorage.getItem(VIEW_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        dualView: parsed.dualView ?? false,
        leftStorageId: parsed.leftStorageId ?? null,
        rightStorageId: parsed.rightStorageId ?? null,
        leftSort: parsed.leftSort ?? "name",
        rightSort: parsed.rightSort ?? "name",
        leftSearch: parsed.leftSearch ?? "",
        rightSearch: parsed.rightSearch ?? "",
      };
    }
  } catch {}
  return { dualView: false, leftStorageId: null, rightStorageId: null, leftSort: "name", rightSort: "name", leftSearch: "", rightSearch: "" };
}

export function saveViewState(state: ViewState) {
  localStorage.setItem(VIEW_KEY, JSON.stringify(state));
}


// What a drag carries: one item when a single tile is dragged, every selected item when the dragged
// tile is part of a selection.
export interface DragSelection {
  sourcePanel: "left" | "right";
  storageId: string;
  items: { itemKey: string; available: number; stack?: ItemStack }[];
}

export interface SlotData {
  item: ItemDef;
  qty: number;
  totalQty: number;
  reserved: number;
  available: number;
  status: string;
  maxCraftable: number;
  hasRecipe: boolean;
  stack?: ItemStack;
}

// An item whose identity includes NBT: no entry in the vanilla catalog, so it carries its own
// definition (name read off the NBT) plus the stack the grid and the tooltip render from.
// baseKey is the catalog entry it varies, so searching the vanilla name finds every NBT variant.
export interface CustomItemEntry {
  def: ItemDef;
  stack: ItemStack;
  baseKey?: string;
  // A GrieferGames recipe makes it, so a search may list it without stock like any vanilla item.
  craftable: boolean;
}

export function toCustomItemEntry(dto: ItemStackDto, resolver: CraftingResolverService): CustomItemEntry {
  const stack = fromItemStackDto(dto);
  const base = resolver.getItemById(dto.itemId, dto.metadata) ?? resolver.getItemById(dto.itemId, 0);
  return {
    baseKey: base?.key,
    def: {
      id: dto.itemId,
      metadata: dto.metadata,
      key: dto.key,
      displayName: resolveItemDisplayName(stack, resolver),
      name: base?.name ?? "",
      stackSize: base?.stackSize ?? 64,
    },
    stack,
    craftable: false,
  };
}

// A registry row as the stack shape the rest of the inventory code reads. Count 1, not 0: to
// ItemStackView a zero count is an empty slot, and the amount is the badge's job anyway.
export function customItemToStackDto(definition: CustomItemDefinition): ItemStackDto {
  return {
    key: itemKey(definition.itemId, definition.metadata, definition.hash),
    itemId: definition.itemId,
    metadata: definition.metadata,
    count: 1,
    snbt: definition.snbt,
    nbtHash: definition.hash,
    isCustom: true,
  };
}

// The custom items a panel may ever show: the ones stocked somewhere, and the ones a GrieferGames
// recipe makes, once per recipe. The registry holds every NBT stack a chest ever showed (auction
// listing copies, one row per serial-numbered perk), so it is not a catalog on its own.
export function selectCraftableCustomItems(
  entries: CustomItemEntry[],
  allSnap: StorageSnapshot,
  hasCustomRecipe: (plainName: string, itemId: number) => boolean,
): CustomItemEntry[] {
  const stocked = (entry: CustomItemEntry) => (allSnap[entry.def.key] ?? 0) > 0;
  const craftable = (entry: CustomItemEntry) => hasCustomRecipe(entry.def.displayName, entry.def.id);
  const recipeOf = (entry: CustomItemEntry) => `${entry.def.displayName}|${entry.def.id}`;

  const visible = entries.filter(stocked).map(entry => ({ ...entry, craftable: craftable(entry) }));
  const representedRecipes = new Set(visible.filter(entry => entry.craftable).map(recipeOf));

  const unstockedByRecipe = new Map<string, CustomItemEntry[]>();
  for (const entry of entries) {
    if (stocked(entry) || !craftable(entry)) continue;
    const recipe = recipeOf(entry);
    if (representedRecipes.has(recipe)) continue;
    const candidates = unstockedByRecipe.get(recipe) ?? [];
    candidates.push(entry);
    unstockedByRecipe.set(recipe, candidates);
  }
  for (const candidates of unstockedByRecipe.values()) {
    candidates.sort((left, right) => left.def.key.localeCompare(right.def.key));
    visible.push({ ...candidates[0], craftable: true });
  }
  return visible;
}

export function sortSlots(slots: SlotData[], mode: SortMode): SlotData[] {
  const sorted = [...slots];
  switch (mode) {
    case "name":
      sorted.sort((a, b) => {
        if (a.qty > 0 && b.qty <= 0) return -1;
        if (a.qty <= 0 && b.qty > 0) return 1;
        return a.item.displayName.localeCompare(b.item.displayName);
      });
      break;
    case "qty-desc":
      sorted.sort((a, b) => b.qty - a.qty || a.item.displayName.localeCompare(b.item.displayName));
      break;
    case "qty-asc":
      sorted.sort((a, b) => {
        if (a.qty === 0 && b.qty > 0) return 1;
        if (a.qty > 0 && b.qty === 0) return -1;
        if (a.qty === 0 && b.qty === 0) return a.item.displayName.localeCompare(b.item.displayName);
        return a.qty - b.qty || a.item.displayName.localeCompare(b.item.displayName);
      });
      break;
    case "id":
      sorted.sort((a, b) => a.item.id - b.item.id || a.item.metadata - b.item.metadata);
      break;
  }
  return sorted;
}

export function computeSlots(
  query: string, snap: StorageSnapshot, allSnap: StorageSnapshot,
  reservations: StorageSnapshot, resolveData: Record<string, ResolveResult>,
  sort: SortMode, resolver: CraftingResolverService,
  recentlyChanged?: Map<string, "up" | "down">, storageId?: string | null,
  customItems: CustomItemEntry[] = []
): SlotData[] {
  const q = query.toLowerCase().trim();
  const allItems = resolver.getAllItems();

  function makeSlot(item: ItemDef, stack?: ItemStack): SlotData {
    const qty = snap[item.key] ?? 0;
    const res = reservations[item.key] ?? 0;
    const rd = resolveData[item.key];
    return {
      item,
      qty,
      totalQty: allSnap[item.key] ?? 0,
      reserved: res,
      available: Math.max(0, qty - res),
      status: rd?.status ?? (qty > 0 ? "InStorage" : "Missing"),
      maxCraftable: rd?.maxCraftable ?? 0,
      hasRecipe: rd?.hasRecipe ?? false,
      // customItems is a cross-storage cache keyed by item key, so its stack.count reflects
      // whichever storage last fetched it, not this one. Rewrite it to this slot's own quantity.
      stack: stack ? { ...stack, count: qty } : stack,
    };
  }

  const inStock = (key: string) =>
    (snap[key] ?? 0) > 0 || !!(storageId && recentlyChanged?.has(`${storageId}:${key}`));

  const customMatches = (entry: CustomItemEntry) =>
    entry.def.displayName.toLowerCase().includes(q) ||
    (entry.baseKey != null && resolver.matchesSearch(entry.baseKey, q));

  let visible: SlotData[];
  if (q) {
    visible = allItems
      .filter((item) => resolver.matchesSearch(item.key, q))
      .map((item) => makeSlot(item));
    // A search shows the vanilla catalog and the craftable GrieferGames items at zero so anything
    // makeable can be looked up. Every other NBT item is stock and nothing else: a perk potion stocked
    // in another storage is not listed here. The unsearched list stays stock-only throughout.
    visible.push(...customItems
      .filter((entry) => (entry.craftable || inStock(entry.def.key)) && customMatches(entry))
      .map((entry) => makeSlot(entry.def, entry.stack)));
  } else {
    visible = allItems
      .filter((item) => inStock(item.key))
      .map((item) => makeSlot(item));
    visible.push(...customItems
      .filter((entry) => inStock(entry.def.key))
      .map((entry) => makeSlot(entry.def, entry.stack)));
  }

  return sortSlots(visible, sort);
}
